import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { 
  Building2, 
  MapPin, 
  Phone, 
  Globe, 
  Briefcase, 
  ArrowRight, 
  Loader2,
  Search,
  Star,
  Users,
  CheckCircle2,
  FileText,
  Sparkles,
  ChevronRight,
  ChevronLeft,
  Info,
  Target,
  BarChart3
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { WizardProgress, WizardInfoPanel } from "@/components/Wizard";
import { SelleTrustAssurance } from "@/components/SelleTrustAssurance";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const industries = [
  "Restaurant & Food Service",
  "Retail & Shopping",
  "Healthcare & Medical",
  "Professional Services",
  "Home Services",
  "Automotive",
  "Beauty & Wellness",
  "Real Estate",
  "Financial Services",
  "Technology",
  "Education",
  "Entertainment",
  "General Business"
];

const wizardSteps = [
  { title: "Find Business", shortTitle: "Find", description: "Search for your business" },
  { title: "Verify Details", shortTitle: "Verify", description: "Confirm business information" },
  { title: "Select Competitors", shortTitle: "Compete", description: "Identify competitors" },
  { title: "Generate Report", shortTitle: "Report", description: "Create analysis" }
];

export default function BusinessLookup() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  
  // Step 1: Search
  const [searchQuery, setSearchQuery] = useState("");
  const [searchLocation, setSearchLocation] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  
  // Step 2: Selected Business
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [businessIndustry, setBusinessIndustry] = useState("");
  
  // Step 3: Competitors
  const [competitors, setCompetitors] = useState([]);
  const [competitorLoading, setCompetitorLoading] = useState(false);
  const [selectedCompetitors, setSelectedCompetitors] = useState([]);
  
  // Step 4: Report
  const [reportGenerating, setReportGenerating] = useState(false);

  // Step 1: Search for business
  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Please enter a business name");
      return;
    }
    
    setLookupLoading(true);
    try {
      const response = await axios.post(`${API}/lookup/business`, {
        business_name: searchQuery,
        location: searchLocation
      });
      
      if (response.data) {
        // Treat as single result for now
        setSearchResults([response.data]);
        toast.success("Business found!");
      }
    } catch (error) {
      toast.error("Business not found. Try different search terms.");
      setSearchResults([]);
    }
    setLookupLoading(false);
  };

  // Step 1: Select a business from results
  const selectBusiness = (business) => {
    setSelectedBusiness(business);
    setCurrentStep(2);
  };

  // Step 2: Confirm business and move to competitors
  const confirmBusiness = async () => {
    if (!businessIndustry) {
      toast.error("Please select an industry");
      return;
    }
    
    setLoading(true);
    try {
      // Save business first
      const response = await axios.post(`${API}/businesses`, {
        name: selectedBusiness.name,
        address: selectedBusiness.address,
        phone: selectedBusiness.phone || "",
        website: selectedBusiness.website || "",
        industry: businessIndustry,
        place_id: selectedBusiness.place_id,
        rating: selectedBusiness.rating,
        review_count: selectedBusiness.review_count
      });
      
      setSelectedBusiness({ ...selectedBusiness, id: response.data.id, industry: businessIndustry });
      setCurrentStep(3);
      
      // Fetch competitors
      fetchCompetitors();
    } catch (error) {
      toast.error("Error saving business. Please try again.");
    }
    setLoading(false);
  };

  // Step 3: Fetch competitors
  const fetchCompetitors = async () => {
    setCompetitorLoading(true);
    try {
      const response = await axios.post(`${API}/lookup/competitors`, {
        business_name: selectedBusiness.name,
        location: searchLocation,
        industry: businessIndustry
      });
      
      if (response.data && response.data.competitors) {
        setCompetitors(response.data.competitors);
      }
    } catch (error) {
      console.error("Error fetching competitors:", error);
    }
    setCompetitorLoading(false);
  };

  // Step 3: Toggle competitor selection
  const toggleCompetitor = (competitor) => {
    if (selectedCompetitors.find(c => c.name === competitor.name)) {
      setSelectedCompetitors(selectedCompetitors.filter(c => c.name !== competitor.name));
    } else {
      setSelectedCompetitors([...selectedCompetitors, competitor]);
    }
  };

  // Step 4: Generate Report
  const generateReport = async () => {
    setReportGenerating(true);
    try {
      const response = await axios.post(`${API}/reports/generate/${selectedBusiness.id}`);
      
      if (response.data && response.data.id) {
        toast.success("Report generated successfully!");
        navigate(`/report/${response.data.id}`);
      }
    } catch (error) {
      toast.error("Error generating report. Please try again.");
    }
    setReportGenerating(false);
  };

  // Navigation
  const goBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return selectedBusiness !== null;
      case 2: return businessIndustry !== "";
      case 3: return true;
      case 4: return true;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="business-lookup-wizard">
      <Navbar />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-8">
            <Badge className="bg-blue-100 text-blue-700 mb-3">BUSINESS ANALYSIS WIZARD</Badge>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">
              Analyze Your Business
            </h1>
            <p className="text-slate-600">
              Follow the steps below to generate a comprehensive reputation report
            </p>
          </div>

          {/* Progress */}
          <WizardProgress 
            currentStep={currentStep} 
            totalSteps={4} 
            steps={wizardSteps}
          />

          {/* Step Content */}
          <Card className="border-slate-200 shadow-lg">
            <CardContent className="p-8">
              
              {/* STEP 1: Search */}
              {currentStep === 1 && (
                <div className="space-y-6" data-testid="step-1">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                      Step 1: Find Your Business
                    </h2>
                    <p className="text-slate-600">
                      Enter your business name and location to search Google Business Profile
                    </p>
                  </div>

                  <WizardInfoPanel icon={Info} variant="info">
                    We use Google Business Profile to find accurate information about your business, 
                    including ratings, reviews, and contact details.
                  </WizardInfoPanel>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium">Business Name</Label>
                      <Input
                        placeholder="e.g., Joe's Coffee Shop"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="mt-1"
                        data-testid="search-name"
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium">Location (City, State)</Label>
                      <Input
                        placeholder="e.g., Austin, TX"
                        value={searchLocation}
                        onChange={(e) => setSearchLocation(e.target.value)}
                        className="mt-1"
                        data-testid="search-location"
                      />
                    </div>
                  </div>

                  <Button 
                    onClick={handleSearch} 
                    disabled={lookupLoading || !searchQuery.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    data-testid="search-btn"
                  >
                    {lookupLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Searching...
                      </>
                    ) : (
                      <>
                        <Search className="w-4 h-4 mr-2" />
                        Search Business
                      </>
                    )}
                  </Button>

                  {/* Search Results */}
                  {searchResults.length > 0 && (
                    <div className="space-y-3">
                      <Label className="text-sm font-medium">Search Results</Label>
                      {searchResults.map((result, idx) => (
                        <Card 
                          key={idx}
                          className={`cursor-pointer transition-all hover:shadow-md ${
                            selectedBusiness?.name === result.name 
                              ? "border-blue-500 bg-blue-50" 
                              : "border-slate-200"
                          }`}
                          onClick={() => selectBusiness(result)}
                          data-testid={`result-${idx}`}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div>
                                <h3 className="font-semibold text-slate-900">{result.name}</h3>
                                <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                  <MapPin className="w-3 h-3" />
                                  {result.address}
                                </p>
                                {result.phone && (
                                  <p className="text-sm text-slate-500 flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {result.phone}
                                  </p>
                                )}
                              </div>
                              <div className="text-right">
                                {result.rating && (
                                  <div className="flex items-center gap-1">
                                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                    <span className="font-semibold">{result.rating}</span>
                                    <span className="text-slate-400 text-sm">({result.review_count})</span>
                                  </div>
                                )}
                                {selectedBusiness?.name === result.name && (
                                  <Badge className="bg-blue-600 mt-2">Selected</Badge>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2: Verify */}
              {currentStep === 2 && selectedBusiness && (
                <div className="space-y-6" data-testid="step-2">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                      Step 2: Verify Business Details
                    </h2>
                    <p className="text-slate-600">
                      Confirm the information below and select your industry
                    </p>
                  </div>

                  <WizardInfoPanel icon={CheckCircle2} variant="success">
                    Selecting the correct industry helps us provide more accurate 
                    benchmarks and competitor analysis.
                  </WizardInfoPanel>

                  {/* Business Summary */}
                  <Card className="bg-slate-50 border-slate-200">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-14 h-14 rounded-xl bg-blue-100 flex items-center justify-center">
                          <Building2 className="w-7 h-7 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-slate-900">{selectedBusiness.name}</h3>
                          <div className="grid md:grid-cols-2 gap-2 mt-2 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              {selectedBusiness.address}
                            </div>
                            {selectedBusiness.phone && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-4 h-4 text-slate-400" />
                                {selectedBusiness.phone}
                              </div>
                            )}
                            {selectedBusiness.website && (
                              <div className="flex items-center gap-2">
                                <Globe className="w-4 h-4 text-slate-400" />
                                {selectedBusiness.website}
                              </div>
                            )}
                            {selectedBusiness.rating && (
                              <div className="flex items-center gap-2">
                                <Star className="w-4 h-4 text-yellow-500" />
                                {selectedBusiness.rating} ({selectedBusiness.review_count} reviews)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Industry Selection */}
                  <div>
                    <Label className="text-sm font-medium">Select Industry *</Label>
                    <Select value={businessIndustry} onValueChange={setBusinessIndustry}>
                      <SelectTrigger className="mt-1" data-testid="industry-select">
                        <SelectValue placeholder="Choose your industry..." />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map((industry) => (
                          <SelectItem key={industry} value={industry}>
                            {industry}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {/* STEP 3: Competitors */}
              {currentStep === 3 && (
                <div className="space-y-6" data-testid="step-3">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                      Step 3: Select Competitors
                    </h2>
                    <p className="text-slate-600">
                      We found similar businesses in your area. Select competitors to compare against.
                    </p>
                  </div>

                  <WizardInfoPanel icon={Target} variant="tip">
                    Competitor analysis helps you understand where you stand in the market 
                    and identify opportunities for improvement.
                  </WizardInfoPanel>

                  {competitorLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                      <span className="ml-3 text-slate-600">Finding competitors...</span>
                    </div>
                  ) : competitors.length > 0 ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium">
                          Nearby Competitors ({selectedCompetitors.length} selected)
                        </Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedCompetitors([])}
                        >
                          Clear Selection
                        </Button>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        {competitors.map((competitor, idx) => (
                          <Card 
                            key={idx}
                            className={`cursor-pointer transition-all hover:shadow-md ${
                              selectedCompetitors.find(c => c.name === competitor.name)
                                ? "border-green-500 bg-green-50" 
                                : "border-slate-200"
                            }`}
                            onClick={() => toggleCompetitor(competitor)}
                          >
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <h4 className="font-medium text-slate-900">{competitor.name}</h4>
                                  <p className="text-xs text-slate-500">{competitor.address}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {competitor.rating && (
                                    <div className="flex items-center gap-1 text-sm">
                                      <Star className="w-3 h-3 text-yellow-500 fill-yellow-500" />
                                      {competitor.rating}
                                    </div>
                                  )}
                                  {selectedCompetitors.find(c => c.name === competitor.name) && (
                                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                                  )}
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-slate-500">
                      No competitors found. You can skip this step.
                    </div>
                  )}
                </div>
              )}

              {/* STEP 4: Generate */}
              {currentStep === 4 && (
                <div className="space-y-6" data-testid="step-4">
                  <div>
                    <h2 className="text-xl font-bold text-slate-900 mb-2">
                      Step 4: Generate Your Report
                    </h2>
                    <p className="text-slate-600">
                      Ready to analyze! We'll create a comprehensive report covering 12 key areas.
                    </p>
                  </div>

                  <WizardInfoPanel icon={BarChart3} variant="info">
                    Your report will include analysis of: Listings, Reviews, Social Media, 
                    Website, SEO, Advertising, E-commerce, Tech Stack, Voice/Phone, AI Chatbot, 
                    Mapping, and Link Profile.
                  </WizardInfoPanel>

                  {/* Summary */}
                  <Card className="bg-gradient-to-br from-blue-50 to-slate-50 border-blue-200">
                    <CardContent className="p-6">
                      <h3 className="font-bold text-slate-900 mb-4">Report Summary</h3>
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Business:</span>
                          <span className="font-medium">{selectedBusiness?.name}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Industry:</span>
                          <span className="font-medium">{businessIndustry}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Location:</span>
                          <span className="font-medium">{selectedBusiness?.address}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-slate-600">Competitors:</span>
                          <span className="font-medium">{selectedCompetitors.length} selected</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* What's Included */}
                  <div>
                    <h4 className="font-medium text-slate-700 mb-3">What's Included:</h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {["Listings", "Reviews", "Social", "Website", "SEO", "Advertising", "Voice", "AI Chatbot", "Mapping", "Linking", "Budget", "AI Insights"].map((item) => (
                        <div key={item} className="flex items-center gap-2 text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-green-500" />
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>

                  <Button
                    onClick={generateReport}
                    disabled={reportGenerating}
                    className="w-full h-14 text-lg bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800"
                    data-testid="generate-report-btn"
                  >
                    {reportGenerating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating Report...
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Full Report
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Navigation */}
              <div className="flex items-center justify-between pt-6 mt-6 border-t border-slate-200">
                <Button
                  variant="outline"
                  onClick={goBack}
                  disabled={currentStep === 1}
                  className="gap-2"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </Button>
                
                {currentStep < 4 ? (
                  <Button
                    onClick={() => {
                      if (currentStep === 2) {
                        confirmBusiness();
                      } else if (currentStep === 3) {
                        setCurrentStep(4);
                      }
                    }}
                    disabled={!canProceed() || loading}
                    className="bg-blue-600 hover:bg-blue-700 gap-2"
                    data-testid="next-btn"
                  >
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        Continue
                        <ChevronRight className="w-4 h-4" />
                      </>
                    )}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>

          {/* SelleTrust Assurance */}
          <div className="mt-12">
            <SelleTrustAssurance compact />
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
